import { useCallback, useEffect, useMemo, useState } from "react";
import {
    UiDialogBody,
    UiDialogContent,
    UiDialogDescription,
    UiDialogFooter,
    UiDialogHeader,
    UiDialogRoot,
    UiDialogTitle,
} from "components/ui/ant/Dialog";
import UuidCell from '../../components/ui/UuidCell';
import ConfirmDialog from "components/ui/ConfirmDialog";
import PaginationFooter from "components/ui/PaginationFooter";
import { LocalizationApi } from "shared/api/localization";
import { notify } from "shared/utils/notify";
import { useListTableState } from '../../shared/hooks/useListTableState';
import {
    Flex,
    Form,
    Input,
    Select,
    App,
    Button,
    Checkbox,
    Layout,
    Switch,
    Table,
    Modal,
} from 'antd';
import { BaseTable } from "../../components/ui/ant/Table";

const { Header, Footer, Sider, Content } = Layout;

const PAGE_SIZE_OPTIONS = [10, 20, 50];
const DEFAULT_LANGS = ["ru", "en"];
const NO_FILTER_VALUE = "__ALL__";
const SEARCH_DEBOUNCE_MS = 450;

function normalizeTags(value) {
    if (!value) return null;
    const list = String(value)
        .split(",")
        .map((tag) => tag.trim())
        .filter(Boolean);
    return list.length ? list : null;
}

function tagsToText(tags) {
    if (!Array.isArray(tags) || tags.length === 0) return "";
    return tags.join(", ");
}

function getTranslationValue(row, langCode) {
    const values = Array.isArray(row?.translations) ? row.translations : [];
    return values.find((x) => x?.langCode === langCode)?.value ?? "";
}

function EditableTextCell({ value, onSave, multiline = false, disabled = false }) {
    const [draft, setDraft] = useState(value ?? "");
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        setDraft(value ?? "");
    }, [value]);

    const commit = async () => {
        if (disabled || saving) return;
        if ((value ?? "") === (draft ?? "")) return;

        setSaving(true);
        try {
            await onSave(draft ?? "");
        } catch (error) {
            setDraft(value ?? "");
            notify.error(error?.message || "Не удалось сохранить изменения");
        } finally {
            setSaving(false);
        }
    };

    const className =
        "w-full rounded border border-transparent bg-transparent px-2 py-1 text-sm " +
        "hover:border-secondary-200 dark:hover:border-secondary-700 " +
        "focus:outline-none focus:border-primary focus:bg-surface disabled:opacity-60";

    if (multiline) {
        return (
            <Input.TextArea
                value={draft}
                rows={2}
                className={className}
                disabled={disabled || saving}
                onChange={(event) => setDraft(event.target.value)}
                onBlur={commit}
            />
        );
    }

    return (
        <Input
            value={draft}
            className={className}
            disabled={disabled || saving}
            onChange={(event) => setDraft(event.target.value)}
            onBlur={commit}
            onKeyDown={(event) => {
                if (event.key === "Enter") event.currentTarget.blur();
            }}
        />
    );
}

function LanguagesDropdown({ options, selected, onChange, disabled }) {
    return (
        <Select
            mode="multiple"
            value={selected}
            onChange={(nextSelected) => {
                if (!nextSelected.length) return;
                onChange(nextSelected);
            }}
            disabled={disabled}
            placeholder="Выбрать языки"
            className="min-w-[220px]"
            optionFilterProp="label"
            options={options.map((lang) => ({
                value: lang.code,
                label: `${lang.code}${lang.name ? ` - ${lang.name}` : ""}`,
            }))}
        />
    );
}

export default function LocalizationPage() {
    const [languages, setLanguages] = useState([]);
    const [selectedLangs, setSelectedLangs] = useState(DEFAULT_LANGS);

    const [projects, setProjects] = useState([]);
    const [versions, setVersions] = useState([]);

    const [projectFilter, setProjectFilter] = useState("");
    const [versionFilter, setVersionFilter] = useState("");
    const [includeUnused, setIncludeUnused] = useState(true);

    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");

    const [createOpen, setCreateOpen] = useState(false);
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [deleteTarget, setDeleteTarget] = useState(null);
    const [reloadKey, setReloadKey] = useState(0);

    const [createForm, setCreateForm] = useState({
        langCode: "ru",
        value: "",
        key: "",
        context: "",
        tags: "",
        project: "",
        version: "",
    });
    const [submitting, setSubmitting] = useState(false);

    const {
        qInput,
        setQInput,
        qDebounced,
        sortBy,
        setSortBy,
        sortDir,
        setSortDir,
        groupBy,
        setGroupBy,
        groupScope,
        setGroupScope,
        page,
        setPage,
        pageSize,
        setPageSize,
        total,
        setTotal,
        totalPages,
        setTotalPages,
        safePage,
    } = useListTableState({
        initialSortBy: 'name',
        initialSortDir: 'asc',
        initialGroupBy: 'none',
        initialPageSize: 6,
        debounceMs: 1450,
        enableGroupScope: true,
    });

    useEffect(() => {
        LocalizationApi.getLanguages()
            .then((data) => {
                const list = Array.isArray(data) ? data : [];
                setLanguages(list);

                if (!list.length) return;

                const defaults = DEFAULT_LANGS.filter((code) => list.some((lang) => lang.code === code));
                if (defaults.length) {
                    setSelectedLangs(defaults);
                    setCreateForm((prev) => ({ ...prev, langCode: defaults[0] }));
                } else {
                    setSelectedLangs([list[0].code]);
                    setCreateForm((prev) => ({ ...prev, langCode: list[0].code }));
                }
            })
            .catch(() => {
                setLanguages([]);
            });

        LocalizationApi.getProjects()
            .then((data) => {
                setProjects(Array.isArray(data) ? data : []);
            })
            .catch(() => {
                setProjects([]);
            });
    }, []);

    useEffect(() => {
        if (!projectFilter) {
            setVersions([]);
            setVersionFilter("");
            return;
        }

        LocalizationApi.getVersions({ project: projectFilter })
            .then((data) => {
                setVersions(Array.isArray(data) ? data : []);
            })
            .catch(() => {
                setVersions([]);
            });
    }, [projectFilter]);

    useEffect(() => {
        if (!selectedLangs.length) {
            setRows([]);
            setTotal(0);
            setTotalPages(1);
            return;
        }

        const ac = new AbortController();
        setLoading(true);
        setError("");

        const numericQuery = Number(qDebounced.trim());
        const hasNumericTrId = qDebounced.trim() && Number.isInteger(numericQuery) && numericQuery > 0;

        LocalizationApi.searchTranslations(
            {
                trIds: hasNumericTrId ? [numericQuery] : undefined,
                project: projectFilter || undefined,
                version: versionFilter || undefined,
                langCodes: selectedLangs,
                value: hasNumericTrId ? undefined : qDebounced.trim() || undefined,
                includeUnused,
                fallbackToDefault: true,
                withTotal: true,
                sort: "trId:desc", // TODO: вынести в UI и позволить пользователю выбирать
                size: pageSize,
                page: Math.max(page - 1, 0),
                search: qDebounced.trim() || undefined,
                pageSize: pageSize,
            },
            { signal: ac.signal },
        )
            .then((response) => {
                if (ac.signal.aborted) return;

                const content = Array.isArray(response?.content) ? response.content : [];
                const pageInfo = response?.page || {};
                setRows(content);
                setTotal(Number(pageInfo.totalElements ?? content.length ?? 0));
                setTotalPages(Math.max(1, Number(pageInfo.totalPages ?? 1)));
            })
            .catch((requestError) => {
                if (ac.signal.aborted) return;
                setError(requestError?.message || "Не удалось загрузить переводы");
            })
            .finally(() => {
                if (!ac.signal.aborted) setLoading(false);
            });

        return () => ac.abort();
    }, [projectFilter, versionFilter, selectedLangs, includeUnused, qDebounced, page, pageSize, reloadKey]);

    const languageChoices = useMemo(() => {
        if (languages.length) return languages;
        return selectedLangs.map((code) => ({ code, name: code.toUpperCase() }));
    }, [languages, selectedLangs]);

    const refresh = () => setReloadKey((prev) => prev + 1);

    const handleProjectFilterChange = (value) => {
        const next = value === NO_FILTER_VALUE ? "" : value;
        setProjectFilter(next);
    };

    const handleVersionFilterChange = (value) => {
        const next = value === NO_FILTER_VALUE ? "" : value;
        setVersionFilter(next);
    };

    const saveMetadata = useCallback(
        async (trId, field, rawValue) => {
            const payload = {};

            if (field === "tags") {
                payload.tags = normalizeTags(rawValue);
            } else if (field === "isUnused") {
                payload.isUnused = Boolean(rawValue);
            } else {
                payload[field] = rawValue === "" ? null : rawValue;
            }

            await LocalizationApi.patchTranslation(trId, payload);

            setRows((prev) =>
                prev.map((row) => {
                    if (row.trId !== trId) return row;
                    if (field === "tags") return { ...row, tags: payload.tags ?? [] };
                    if (field === "isUnused") return { ...row, isUnused: payload.isUnused };
                    return { ...row, [field]: payload[field] };
                }),
            );
        },
        [],
    );

    const saveValue = useCallback(async (trId, langCode, value) => {
        const payload = { value: value ?? "" };
        await LocalizationApi.upsertTranslationValue(trId, langCode, payload);

        setRows((prev) =>
            prev.map((row) => {
                if (row.trId !== trId) return row;
                const translations = Array.isArray(row.translations) ? [...row.translations] : [];
                const idx = translations.findIndex((item) => item.langCode === langCode);

                if (idx >= 0) {
                    translations[idx] = { ...translations[idx], value: payload.value };
                } else {
                    translations.push({ langCode, value: payload.value });
                }

                return { ...row, translations };
            }),
        );
    }, []);

    const tableColumns = useMemo(() => {
        const staticColumns = [
            {
                header: "trId",
                key: "trId",
                width: 110,
                render: (trId) => <UuidCell value={trId}></UuidCell>,
            },
            {
                header: "key",
                dataIndex: "key",
                key: "key",
                width: 220,
                render: (_, row) => (
                    <EditableTextCell
                        value={row.key ?? ""}
                        onSave={(next) => saveMetadata(row.trId, "key", next)}
                    />
                ),
            },
            {
                header: "tags",
                dataIndex: "tags",
                key: "tags",
                width: 220,
                render: (_, row) => (
                    <EditableTextCell
                        value={tagsToText(row.tags)}
                        onSave={(next) => saveMetadata(row.trId, "tags", next)}
                    />
                ),
            },
            {
                header: "context",
                dataIndex: "context",
                key: "context",
                width: 260,
                render: (_, row) => (
                    <EditableTextCell
                        value={row.context ?? ""}
                        multiline
                        onSave={(next) => saveMetadata(row.trId, "context", next)}
                    />
                ),
            },
        ];

        const translationColumns = selectedLangs.map((lang) => ({
            header: `value_${lang}`,
            key: `value_${lang}`,
            width: 260,
            render: (_, row) => (
                <EditableTextCell
                    value={getTranslationValue(row, lang)}
                    multiline
                    onSave={(next) => saveValue(row.trId, lang, next)}
                />
            ),
        }));

        const controlColumns = [
            {
                header: "isUnused",
                dataIndex: "isUnused",
                key: "isUnused",
                align: "center",
                width: 120,
                render: (_, row) => (
                    <Checkbox
                        checked={Boolean(row.isUnused)}
                        onChange={(event) => saveMetadata(row.trId, "isUnused", event.target.checked)}
                    />
                ),
            },
            {
                header: "Действия",
                key: "actions",
                align: "center",
                width: 150,
                render: (_, row) => (
                    <button
                        type="button"
                        className="btn btn-outline text-red-600 border-red-200 hover:bg-red-50 disabled:opacity-50"
                        disabled={!row.isUnused}
                        onClick={() => setDeleteTarget({ trId: row.trId, key: row.key })}
                    >
                        Удалить
                    </button>
                ),
            },
        ];

        return [...staticColumns, ...translationColumns, ...controlColumns];
    }, [selectedLangs, saveMetadata, saveValue]);

    const handleCreate = async () => {
        if (!createForm.langCode || !createForm.value.trim()) {
            notify.error("Заполните язык и значение перевода");
            return;
        }
        try {
            const created = await LocalizationApi.createTranslation({
                langCode: createForm.langCode,
                value: createForm.value.trim(),
                project: createForm.project.trim() || undefined,
                version: createForm.version.trim() || undefined,
                key: createForm.key.trim() || null,
                context: createForm.context.trim() || null,
                tags: normalizeTags(createForm.tags),
            });
            notify.success("Перевод создан");
            setCreateOpen(false);
            setCreateForm((prev) => ({
                ...prev,
                value: "",
                key: "",
                context: "",
                tags: "",
            }));
            setSubmitting(true);
            setPage(1);
            refresh();
        } catch (error) {
            notify.error("Не удалось создать перевод");
            return;
        } finally {
            setSubmitting(false);
        }
    };

    const handleDelete = async () => {
        if (!deleteTarget) return;

        await LocalizationApi.deleteTranslation(deleteTarget.trId);
        notify.success("Перевод удален");

        setDeleteTarget(null);
        refresh();
    };

    const tablePagination = {
        showQuickJumper: false,
        showSizeChanger: true,
        pageSizeOptions: [6, 12, 18, 24],
        current: safePage,
        pageSize: pageSize,
        total: total,
    };

    const handleTableChange = (paginationInfo, filters, sorter, extra) => {
        onChange?.(paginationInfo, filters, sorter, extra);

        if (onSortChange && sorter && !Array.isArray(sorter)) {
            const dir = sorter.order === 'descend' ? 'desc' : 'asc';
            onSortChange(sorter.order ? sorter.columnKey : undefined, dir);
        }
    };

    return (
        <App>
            <Flex direction="column" className="w-full overflow-x-auto">
                <section className="w-full p-4 md:p-6 space-y-4">
                    <header className="flex flex-col gap-3 md:flex-row md:items-end md:justify-between">
                        <div className="space-y-1">
                            <h2 className="text-xl font-semibold text-text-primary">Локализация</h2>
                            <p className="text-sm text-text-secondary">
                                `project/version` используются как опциональные фильтры и по умолчанию не отправляются
                            </p>
                        </div>

                        <button
                            type="button"
                            className="btn btn-soft-accent"
                            onClick={() => setCreateOpen(true)}
                        >
                            Добавить перевод
                        </button>
                    </header>

                    <Flex align="end" justify="start" flex wrap="wrap" gap="large" className="w-full">
                        <Flex vertical gap="1">
                            <label className="eyebrow">Поиск</label>
                            <Input
                                value={qInput}
                                onChange={(event) => setQInput(event.target.value)}
                                className="Input w-64"
                                placeholder="trId или текст перевода"
                            />
                        </Flex>

                        <Flex vertical gap="1">
                            <label className="eyebrow">Project (optional)</label>
                            <Select
                                value={projectFilter || NO_FILTER_VALUE}
                                onChange={handleProjectFilterChange}
                                placeholder="Без фильтра"
                                className="w-56"
                                options={[
                                    { value: NO_FILTER_VALUE, label: "Без фильтра" },
                                    ...projects.map((project) => ({ value: project, label: project })),
                                ]}
                            />
                        </Flex>

                        <Flex vertical gap="1" >
                            <label className="eyebrow">Version (optional)</label>
                            <Select
                                value={versionFilter || NO_FILTER_VALUE}
                                onChange={handleVersionFilterChange}
                                placeholder="Без фильтра"
                                className="w-44"
                                disabled={!projectFilter}
                            >
                                <Select.Option value={NO_FILTER_VALUE}>Без фильтра</Select.Option>
                                {versions.map((version) => (
                                    <Select.Option key={version} value={version}>{version}</Select.Option>
                                ))}
                            </Select>
                        </Flex>

                        <Flex vertical gap="1">
                            <label className="eyebrow">Языки</label>
                            <LanguagesDropdown
                                options={languageChoices}
                                selected={selectedLangs}
                                onChange={setSelectedLangs}
                                disabled={!languageChoices.length}
                            />
                        </Flex>

                        <Flex vertical gap="1">
                            <label className="eyebrow">На странице</label>
                            <Select
                                value={String(pageSize)}
                                onChange={(value) => setPageSize(Number(value))}
                                className="w-20"
                                options={PAGE_SIZE_OPTIONS.map((n) => ({ value: String(n), label: String(n) }))}
                            />
                        </Flex>

                        <Flex vertical gap="1">
                            <label className="eyebrow">Показывать isUnused</label>
                            <Switch
                                checked={includeUnused}
                                onChange={(checked) => setIncludeUnused(checked)}
                                className="w-14"
                                placeholder="Показывать isUnused"
                            />
                        </Flex>
                    </Flex>

                    {error && <p className="text-sm text-red-500">{error}</p>}

                    <div className="overflow-hidden rounded-lg border border-secondary-200 dark:border-secondary-700">
                        <BaseTable
                            columns={tableColumns}
                            data={rows}
                            searchQuery={qInput}
                            searchKeys={["trId", "key", "context", "tags"]}
                            sortBy={sortBy}
                            sortDir={sortDir}
                            groupBy={groupBy ==="none" ? undefined : groupBy}
                            onSortChange={(nextSortBy, nextSortDir) => {
                                setSortBy(nextSortBy);
                                setSortDir(nextSortDir);
                            }}
                            pagination={{ page, pageSize, total }}
                            onChange={(paginationInfo, filters, sorter, extra) => {
                                if (paginationInfo) {
                                    setPage(paginationInfo.current);
                                    setPageSize(paginationInfo.pageSize);
                                }
                                if (sorter && !Array.isArray(sorter)) {
                                    const dir = sorter.order === 'descend' ? 'desc' : 'asc';
                                    setSortBy(sorter.order ? sorter.columnKey : undefined);
                                    setSortDir(dir);
                                }
                            }}
                            onGroupByChange={(nextGroupBy) => {
                                setGroupBy(nextGroupBy);
                            }}
                            groupByResolver={(row) => {
                                if (groupBy === "project") return row.project || "(no project)";
                                if (groupBy === "version") return row.version || "(no version)";
                                if (groupBy === "isUnused") return row.isUnused ? "isUnused" : "used";
                                return undefined;
                            }}
                            groupLabelResolver={(groupKey) => {
                                if (groupBy === "project") return `Project: ${groupKey}`;
                                if (groupBy === "version") return `Version: ${groupKey}`;
                                if (groupBy === "isUnused") return groupKey === "isUnused" ? "isUnused" : "used";
                                return groupKey;
                            }}
                            
                        >
                        </BaseTable>
                    </div>

                    <UiDialogRoot open={createOpen} onOpenChange={setCreateOpen}>
                        <UiDialogContent size="md">
                            <UiDialogHeader>
                                <div>
                                    <UiDialogTitle>Новый перевод</UiDialogTitle>
                                    <UiDialogDescription>
                                        Project/Version для создания заполняются отдельно и не связаны с фильтром
                                    </UiDialogDescription>
                                </div>
                            </UiDialogHeader>

                            <UiDialogBody className="space-y-4">
                                <Form onSubmit={handleCreate} layout="vertical" className="flex flex-col items-stretch" >
                                    <div className="grid gap-4 md:grid-cols-2">
                                        <div className="flex flex-col gap-1">
                                            <label className="eyebrow">Язык</label>
                                            <Select
                                                value={createForm.langCode}
                                                onChange={(value) => setCreateForm((prev) => ({ ...prev, langCode: value }))}
                                            >
                                                {languageChoices.map((lang) => (
                                                    <Select.Option key={lang.code} value={lang.code}>{lang.code}</Select.Option>
                                                ))}
                                            </Select>
                                        </div>

                                        <div className="flex flex-col gap-1">
                                            <label className="eyebrow">Key</label>
                                            <Input
                                                value={createForm.key}
                                                onChange={(event) => setCreateForm((prev) => ({ ...prev, key: event.target.value }))}
                                                className="Input"
                                                placeholder="Например: home.title"
                                            />
                                        </div>
                                    </div>

                                    <div className="grid gap-4 md:grid-cols-2">
                                        <div className="flex flex-col gap-1">
                                            <label className="eyebrow">Project</label>
                                            <Input
                                                value={createForm.project}
                                                onChange={(event) => setCreateForm((prev) => ({ ...prev, project: event.target.value }))}
                                                className="Input"
                                                placeholder="metaclicker"
                                            />
                                        </div>

                                        <div className="flex flex-col gap-1">
                                            <label className="eyebrow">Version</label>
                                            <Input
                                                value={createForm.version}
                                                onChange={(event) => setCreateForm((prev) => ({ ...prev, version: event.target.value }))}
                                                className="Input"
                                                placeholder="1.0"
                                            />
                                        </div>
                                    </div>

                                    <div className="flex flex-col gap-1">
                                        <label className="eyebrow">Значение</label>
                                        <Input.TextArea
                                            rows={3}
                                            value={createForm.value}
                                            onChange={(event) => setCreateForm((prev) => ({ ...prev, value: event.target.value }))}
                                            className="Input"
                                            placeholder="Текст перевода"
                                        />
                                    </div>

                                    <div className="flex flex-col gap-1">
                                        <label className="eyebrow">Context</label>
                                        <Input.TextArea
                                            rows={2}
                                            value={createForm.context}
                                            onChange={(event) => setCreateForm((prev) => ({ ...prev, context: event.target.value }))}
                                            className="Input"
                                            placeholder="Пояснение для переводчика"
                                        />
                                    </div>

                                    <div className="flex flex-col gap-1">
                                        <label className="eyebrow">Tags (через запятую)</label>
                                        <Input
                                            value={createForm.tags}
                                            onChange={(event) => setCreateForm((prev) => ({ ...prev, tags: event.target.value }))}
                                            className="Input"
                                            placeholder="ui, menu, labels"
                                        />
                                    </div>
                                </Form>
                            </UiDialogBody>

                            <UiDialogFooter>
                                <Button
                                    type="button"
                                    className="btn btn-soft-accent"
                                    onClick={handleCreate}
                                >
                                    {submitting ? "Создаем..." : "Создать"}
                                </Button>
                                <Button type="button" className="btn btn-outline" onClick={() => setCreateOpen(false)}>
                                    Отмена
                                </Button>
                            </UiDialogFooter>
                        </UiDialogContent>
                    </UiDialogRoot>

                    <Modal
                        open={deleteDialogOpen}
                        onCancel={() => { setDeleteDialogOpen(false); setDeleteTarget(null); }}
                        title="Удалить параметр?"
                        closable={false}
                        centered
                        width=""
                        className=""
                        styles={{
                            body: { padding: 0 },
                            content: { overflow: "hidden" },
                        }}
                        onOk={() => { setDeleteDialogOpen(false); setDeleteTarget(null); handleDelete(); }}
                        footer={(_, { OkBtn, CancelBtn }) => (
                            <>
                                <OkBtn />
                                <CancelBtn />
                            </>
                        )}
                    >
                        {deleteTarget
                            ? `Будет удален trId=${deleteTarget.trId}${deleteTarget.key ? ` (key: ${deleteTarget.key})` : ""}.`
                            : ""}
                    </Modal>
                </section>
            </Flex>
        </App>
    );
}
